import React, { Fragment } from "react";

import { getImage } from "gatsby-plugin-image";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { BgImage } from "gbimage-bridge";

import { blue, red, white } from "../../../../utils/colors";
import LinkComponent from "../../LinkComponent";
import Markdown from "../../Markdown";
import HeadlineItem from "../ContentElement/HeadlineItem";
import { SideNavContainer } from "../SideNavContainer";
import useSideNavStore from "../SideNavContainer/store";

const Container = styled.div`
    display: flex;
    position: relative;
    overflow: hidden;
    ${({ theme }) => theme.breakpoints.from.small.css`
          column-gap: 50px;
    `}

    margin-top: -80px;
    padding-top: 80px;
`;

const RelativeContainer = styled.div`
    position: relative;
`;

const LeftContentContainer = styled.div`
    z-index: 1;
    position: relative;
    ${({ theme }) => theme.paddings.containerOuter.left}
    ${({ theme }) => theme.paddings.containerOuter.right}


    display: flex;
    flex-direction: column;
    justify-content: space-between;
    ${({ theme }) => theme.breakpoints.from.medium.css`
        flex-direction: row;
    `}
`;

const LeftShapeContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: -600px;
    background-color: ${red};
    border-radius: 40px;
`;

const RightContentContainer = styled.div`
    position: relative;
    left: -300px;
    z-index: 1;
    height: 100%;
`;

const RightShapeContainer = styled.div`
    ${({ theme }) => theme.breakpoints.upTo.extraSmall.css`
        display: none;
    `}

    border-radius: 40px;
    position: absolute;
    right: -600px;
    top: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;
`;

const LinkWrapper = styled.div`
    display: block;
    margin: 20px 0;
`;

const ItemContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const DoubleShape = ({
    items,
    shapeRight,
    width,
    image: rawImage,
    paddingTop = 0,
    paddingBottom = 0,
    paddingRight = 0,
    id,
}) => {
    const theme = useTheme();
    const [paddingLeft] = useSideNavStore((state) => [state.paddingLeft]);

    const skewHelper = (after = false) => {
        if (after) {
            return !shapeRight ? "skew(-32deg)" : "skew(32deg)";
        }
        return !shapeRight ? "skew(32deg)" : "skew(-32deg)";
    };

    const BackgroundColorBlue = css`
        background-color: ${blue};
    `;

    const widthHelper = (right = false) => {
        if (width === "2") {
            if (!right) {
                return `${theme.breakpoints.from.small.css`
                           width: 90%
                `}`;
            }
            return `width: 7%;`;
        }
        if (!right) {
            return css`
                ${theme.breakpoints.propPerEachFrom("width", {
                    extraLarge: "50%",
                    large: "50%",
                    normal: "60%",
                    medium: "70%",
                    small: "80%",
                    extraSmall: "100%",
                })}
            `;
        }
        return css`
            ${theme.breakpoints.propPerEachFrom("width", {
                extraLarge: "45%",
                large: "45%",
                normal: "35%",
                medium: "25%",
                small: "15%",
                extraSmall: "0%",
            })}
        `;
    };

    const leftContainerStyle = css`
        ${widthHelper()};
    `;

    const leftShapeContainerStyle = css`
        ${theme.breakpoints.from.small.css`
          transform: ${skewHelper()};
        `}
        ${theme.breakpoints.upTo.extraSmall.css`
             right: -110px;
        `}
    `;

    const rightContainerStyle = css`
        ${widthHelper(true)};
    `;

    const rightShapeContainerStyle = css`
        ${theme.breakpoints.upTo.extraSmall.css`
             left: 120px;
        `}

        transform: ${skewHelper()};
    `;

    const rightContentContainerStyle = css`
        transform: ${skewHelper(true)};
    `;

    const Text = styled(Markdown)`
        margin: 20px 0;
        font-size: 1rem;
        color: ${white};

        &,
        * {
            color: ${white} !important;
        }
    `;

    const paddingContent = css`
        ${theme.breakpoints.from.medium.css`
            padding-top: ${paddingTop};
            padding-right: ${paddingRight};
            padding-bottom: ${paddingBottom};
        `}
    `;

    const rightPadding = css`
        ${theme.breakpoints.from.small.css`
            padding-right: 150px;
        `}
    `;

    const itemContainerStyle = css`
        ${theme.breakpoints.from.medium.css`
           width: 40%;
        `}
    `;

    const getFontSize = (headline) => {
        switch (headline) {
            case "1":
                return "4rem";
            case "2":
                return "3.5rem";
            case "3":
                return "3rem";
            case "4":
                return "2.5rem";
            case "5":
                return "2rem";
            default:
                return "2rem";
        }
    };

    const getFontSizeExtraSmall = (headline) => {
        switch (headline) {
            case "1":
                return "3rem";
            case "2":
                return "2.5rem";
            case "3":
                return "2rem";
            case "4":
                return "1.5rem";
            case "5":
                return "1rem";
            default:
                return "1rem";
        }
    };

    const getPadding = (index) => {
        if (shapeRight && index === 1) {
            return rightPadding;
        }
        if (!shapeRight && index === 0) {
            return rightPadding;
        }
    };

    const image = rawImage?.src && getImage(rawImage.src);

    return (
        <Container id={id}>
            <RelativeContainer css={leftContainerStyle}>
                <LeftContentContainer css={paddingContent}>
                    {items.length > 0 &&
                        items.map((item, index) => {
                            return (
                                <ItemContainer
                                    key={index}
                                    css={css`
                                        ${items.length > 1 &&
                                        itemContainerStyle}
                                        ${getPadding(index)}
                                    `}
                                >
                                    <SideNavContainer paddingLeft={paddingLeft}>
                                        <Fragment>
                                            {item.name && (
                                                <HeadlineItem
                                                    color="white"
                                                    wordBreak={item.wordBreak}
                                                    headline={
                                                        item.headlineNumber
                                                    }
                                                    fontSize={getFontSize(
                                                        item.headlineNumber
                                                    )}
                                                    fSExtraSmall={getFontSizeExtraSmall(
                                                        item.headlineNumber
                                                    )}
                                                >
                                                    {item.name}
                                                </HeadlineItem>
                                            )}
                                            {item.text && (
                                                <Text>{item.text}</Text>
                                            )}
                                            {item.link &&
                                                item.link.map((link, i2) => (
                                                    <LinkWrapper key={i2}>
                                                        <LinkComponent
                                                            link={link}
                                                            color="white"
                                                            underline
                                                            anchor={link.anchor}
                                                        />
                                                    </LinkWrapper>
                                                ))}
                                        </Fragment>
                                    </SideNavContainer>
                                </ItemContainer>
                            );
                        })}
                </LeftContentContainer>
                <LeftShapeContainer css={leftShapeContainerStyle} />
            </RelativeContainer>
            <RelativeContainer css={rightContainerStyle}>
                <RightShapeContainer
                    css={css`
                        ${rightShapeContainerStyle}
                        ${!image && BackgroundColorBlue}
                    `}
                >
                    <RightContentContainer
                        as={BgImage}
                        {...{ image }}
                        css={css`
                            ${rightContentContainerStyle}
                            ${image &&
                            css`
                                background-repeat: no-repeat;
                                background-size: cover;
                            `};
                        `}
                    />
                </RightShapeContainer>
            </RelativeContainer>
        </Container>
    );
};

export default DoubleShape;
